import { ManualLocales } from "core/hooks/useLocale/useLocale";

import ErrorAlert from "shared/UI/Alerts/ErrorAlert";
import { Hideable } from "shared/UI/Hideable/Hideable";
import { LocaleOption } from "UIPalette/LocaleModal/LocaleModal";
import { Modal } from "shared/UI/Modal/Modal";
import Button from "shared/UI/Buttons/Button";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { changeLanguage } from "i18next";
import { trackBtnClicks, trackEvents } from "core/monitoring/Events";
import { EventNames } from "core/monitoring/types/enums";
import { Input } from "shared/UI/Input/Input";

export type EnterClientCodeProps = {
  handleClick: Function;
  error?: string;
  initialClientCode: string | undefined;
};

const ButtonWithTitle = ({ title, text, onClick }: { title: string; text: string; onClick: () => any }) => (
  <div>
    <p>{title}</p>
    {/* Added the existing font-size to accept accessibility for now until a new design is implemented */}
    <button className="m-2 text-[14pt] text-brand font-bold" role="link" onClick={onClick} aria-label={text}>
      {text}
    </button>
  </div>
);

export const EnterClientCode = ({ handleClick, error, initialClientCode }: EnterClientCodeProps) => {
  const { t } = useTranslation();
  const [clientCode, setClientCode] = useState(initialClientCode ?? "");
  const [helpOpen, setHelpOpen] = useState(false);

  const handleChange = ({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => {
    setClientCode(value);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleClick(clientCode);
    }
  };

  const handleModalOpen = () => {
    trackBtnClicks(EventNames.FIND_OUT_MORE);
    setHelpOpen(true);
  };

  const handleLocaleSelect = (locale: ManualLocales) => {
    trackEvents(EventNames.FIND_OUT_MORE_LOCALE_SELECT);
    changeLanguage(locale);
  };

  return (
    <>
      <div className="flex flex-col gap-6">
        <h1 className="pb-4 text-center">{t("auth.heading")}</h1>
        <div className="space-y-4">
          <Input
            aria-label={t("auth.clientCodeLabel")}
            placeholder={t("auth.clientCodePlaceholder")}
            type="text"
            value={clientCode}
            onChange={handleChange}
            onKeyUp={handleKeyPress}
          />
          {error ? <ErrorAlert title={t("auth.confirmCodeErrorTitle")} content={error} /> : null}
          <Button text={t("auth.button")} onClick={() => handleClick(clientCode)} />
        </div>
        <div className="text-center">
          <ButtonWithTitle title={t("auth.helpText")} text={t("auth.helpButtonText")} onClick={handleModalOpen} />
        </div>
      </div>

      <Hideable hidden={!helpOpen}>
        <Modal width="xs" close={() => setHelpOpen(false)}>
          <div className="p-6 text-center space-y-4">
            <h2>{t("auth.helpModal.title")}</h2>
            <p>{t("auth.helpModal.body")}</p>
            <h3>{t("auth.helpModal.contactTitle")}</h3>

            <div className="space-y-4">
              <LocaleOption handleLocaleClick={handleLocaleSelect} />
              <a
                href={`tel:${t("auth.helpModal.phoneNumber")!}`}
                className="block no-underline"
                onClick={() => trackEvents(EventNames.PHONE_CALL_START, { source: EventNames.FIND_OUT_MORE })}
              >
                <Button text={t("auth.helpModal.phoneNumber")!} />
              </a>
              <a
                href={`mailto:${t("auth.helpModal.email")!}`}
                className="block no-underline"
                onClick={() => trackEvents(EventNames.EMAIL_START, { source: EventNames.FIND_OUT_MORE })}
              >
                <Button variant="secondary" text={t("auth.helpModal.emailTitle")!} />
              </a>
            </div>
          </div>
        </Modal>
      </Hideable>
    </>
  );
};
